/**
 * analytics.js — one place for every GA4 / GTM call in the app.
 *
 * The tags themselves live in index.html:
 *   GA4  : G-50ZT99RCPV   (loaded via gtag.js)
 *   GTM  : GTM-NX3H4S9J   (loaded via gtm.js)
 *
 * Both share one queue: window.dataLayer. That array is created synchronously
 * in <head>, so calling anything here BEFORE the tags finish downloading is
 * safe — GTM and gtag replay whatever is already in the queue on startup.
 *
 * Two consumers read that queue differently:
 *   - gtag('event', name, params)      -> goes straight to GA4, no GTM setup needed
 *   - dataLayer.push({ event: name })  -> fires a GTM "Custom Event" trigger
 *
 * We do both so events work today (GA4) and are available to GTM tags later.
 * If you build a GA4 Event tag inside GTM for a name we already send here,
 * flip GA4_DIRECT to false or that event will be counted twice.
 */

const GA4_DIRECT = true;

const isBrowser = () => typeof window !== 'undefined';
const isDev = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV;

/** Raw push onto the GTM queue. */
export function pushToDataLayer(payload) {
  if (!isBrowser()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
  if (isDev) console.debug('[analytics] dataLayer', payload);
}

/** Send straight to GA4 via gtag (defined in index.html). */
function gtagEvent(name, params) {
  if (!isBrowser() || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
}

/**
 * Track a custom event.
 * @param {string} name   GA4 event name: snake_case, <=40 chars.
 * @param {object} params Event parameters. Keys snake_case, values short strings/numbers.
 */
export function trackEvent(name, params = {}) {
  if (!name) return;
  const payload = { ...params };
  if (GA4_DIRECT) gtagEvent(name, payload);
  pushToDataLayer({ event: name, ...payload });
}

/**
 * Track a virtual page view after a client-side route change.
 * GA4's Enhanced Measurement already catches history changes, so this pushes a
 * distinctly-named event for GTM triggers rather than a second GA4 page_view.
 */
export function trackPageView({ path, title } = {}) {
  if (!isBrowser()) return;
  const page_path = path || window.location.pathname + window.location.search;
  const page_title = title || document.title;

  pushToDataLayer({
    event: 'spa_page_view',
    page_path,
    page_title,
    page_location: window.location.href,
  });

  // Keep the GA4 tag's notion of "current page" in sync for events fired after
  // this point (GA4 otherwise keeps the landing URL for the whole session).
  if (isBrowser() && typeof window.gtag === 'function') {
    window.gtag('set', { page_path, page_title, page_location: window.location.href });
  }
}

/** A lead form was submitted successfully. GA4 recommended event. */
export function trackFormSubmit(formName, params = {}) {
  trackEvent('generate_lead', { form_name: formName, ...params });
}

/** A CTA / button was clicked. */
export function trackCtaClick(label, params = {}) {
  trackEvent('cta_click', { cta_label: label, ...params });
}

/** A click that leaves the site (WhatsApp, mail, social, calendar). */
export function trackOutboundClick(label, url, params = {}) {
  trackEvent('outbound_click', { link_label: label, link_url: url, ...params });
}
